import {useReducer} from 'react';

import {HandlerOf, PredicateOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DelayParam} from '../../../../../editors/commons/paramModel';
import {ClientRuleAction} from '../../../../ruleActionModel';
import {CsatEditorActionTypesEnum} from '../state/csatEditorActions';
import {csatEditorSpecificReducer} from '../state/csatEditorReducer';
import {CsatEditorSpecificState, CsatEditorSubsetsEnum} from '../state/csatEditorState';

export interface CsatEditorSpecificActions {
  onSetDelaySinceTrigger: HandlerOf<DelayParam>;
  onSetResponseId: HandlerOf<number>;

  onSetScheduledTag: HandlerOf<number>;
  onSetSentTag: HandlerOf<number>;

  onAddScheduledAction: HandlerOf<ClientRuleAction>;
  onUpdateScheduledAction: (
    predicate: PredicateOf<ClientRuleAction>,
    update: (action: ClientRuleAction) => ClientRuleAction,
  ) => void;
  onDeleteScheduledAction: (predicate: PredicateOf<ClientRuleAction>) => void;

  onAddSentAction: HandlerOf<ClientRuleAction>;
  onUpdateSentAction: (
    predicate: PredicateOf<ClientRuleAction>,
    update: (action: ClientRuleAction) => ClientRuleAction,
  ) => void;
  onDeleteSentAction: (predicate: PredicateOf<ClientRuleAction>) => void;
}

export function useCsatEditorSpecificState(
  intialState: CsatEditorSpecificState,
): CsatEditorSpecificActions & {csatEditorState: CsatEditorSpecificState} {
  const [csatEditorState, dispatch] = useReducer(csatEditorSpecificReducer, intialState);

  const onSetDelaySinceTrigger = (delay: DelayParam) => {
    dispatch({type: CsatEditorActionTypesEnum.SET_DELAY_SINCE_TRIGGER, payload: delay});
  };

  const onSetResponseId = (responseId: number) => {
    dispatch({type: CsatEditorActionTypesEnum.SET_RESPONSE, payload: responseId});
  };

  const onSetScheduledTag = (tagId: number) => {
    dispatch({
      type: CsatEditorActionTypesEnum.SET_TAG,
      payload: {tagId, subset: CsatEditorSubsetsEnum.SCHEDULED},
    });
  };

  const onSetSentTag = (tagId: number) => {
    dispatch({type: CsatEditorActionTypesEnum.SET_TAG, payload: {tagId, subset: CsatEditorSubsetsEnum.SENT}});
  };

  function makeOnAddAction(subset: CsatEditorSubsetsEnum) {
    return (action: ClientRuleAction) => {
      dispatch({type: CsatEditorActionTypesEnum.ADD_ACTION, payload: {action, subset}});
    };
  }

  const onAddScheduledAction = makeOnAddAction(CsatEditorSubsetsEnum.SCHEDULED);
  const onAddSentAction = makeOnAddAction(CsatEditorSubsetsEnum.SENT);

  function makeOnUpdateAction(subset: CsatEditorSubsetsEnum) {
    return (
      predicate: PredicateOf<ClientRuleAction>,
      update: (action: ClientRuleAction) => ClientRuleAction,
    ) => {
      dispatch({type: CsatEditorActionTypesEnum.UPDATE_ACTION, payload: {predicate, update, subset}});
    };
  }

  const onUpdateScheduledAction = makeOnUpdateAction(CsatEditorSubsetsEnum.SCHEDULED);
  const onUpdateSentAction = makeOnUpdateAction(CsatEditorSubsetsEnum.SENT);

  function makeOnDeleteAction(subset: CsatEditorSubsetsEnum) {
    return (predicate: PredicateOf<ClientRuleAction>) => {
      dispatch({type: CsatEditorActionTypesEnum.DELETE_ACTION, payload: {predicate, subset}});
    };
  }

  const onDeleteScheduledAction = makeOnDeleteAction(CsatEditorSubsetsEnum.SCHEDULED);
  const onDeleteSentAction = makeOnDeleteAction(CsatEditorSubsetsEnum.SENT);

  return {
    csatEditorState,

    onSetDelaySinceTrigger,
    onSetResponseId,
    onSetScheduledTag,
    onSetSentTag,

    onAddScheduledAction,
    onAddSentAction,

    onUpdateScheduledAction,
    onUpdateSentAction,

    onDeleteScheduledAction,
    onDeleteSentAction,
  };
}
